import React, { createContext, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSnackbarContext } from "../SnackbarContext";
import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";
import { setLoginData } from "../../redux/Reducers/AuthReducer/authSplice";
import DepositModal from "../../screens/BuyCoins/DepositModal";
import PaymentModal from "../../screens/BuyCoins/PaymentModal";
import { fetchCurrencyList } from "../../screens/BuyCoins/CurrencyList";

const QuickDepositContext = createContext({ openQuickDeposit: () => {} });

export const useQuickDeposit = () => useContext(QuickDepositContext);

/**
 * Owns the whole quick-deposit flow so it can be opened from anywhere without
 * navigating: the deposit popup, the config fetch, and the handoff to the
 * existing PaymentModal (which keeps crypto choice, QR and polling).
 */
export const QuickDepositProvider = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showErrorSnackbar } = useSnackbarContext();
  const IsLoginData = useSelector((state) => state.auth.IsLoginData);

  const [open, setOpen] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [config, setConfig] = useState(null);
  const [chips, setChips] = useState(null);
  const [minDeposit, setMinDeposit] = useState(null);

  const [payModal, setPayModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState({});
  const [initialCrypto, setInitialCrypto] = useState(null);
  const [paymentMode, setPaymentMode] = useState([]);

  const refreshUser = async () => {
    if (!IsLoginData?.idUser) return;
    try {
      const res = await getAllData(
        `${endPoints.api.GET_BY_USERID}/${IsLoginData.idUser}`
      );
      dispatch(setLoginData({ user: res?.data, isAuthenticated: true }));
    } catch (e) {
      console.error("quick deposit user refresh failed", e);
    }
  };

  const openQuickDeposit = async () => {
    setOpen(true);

    // Always refetch: admin can change chips/multiplier at any time. The API
    // converts the USD config into the player's currency (exchange_rate x
    // multifactor) and returns the USD originals alongside.
    try {
      const userCurrency = IsLoginData?.currency || "USD";
      const res = await getAllData(
        `${endPoints.api.MANUAL_DEPOSIT_CONFIG}?currency=${encodeURIComponent(
          userCurrency
        )}`
      );
      if (res?.status === "success" && res?.data) {
        setConfig(res.data);
        setChips([
          Number(res.data.chip_one),
          Number(res.data.chip_two),
          Number(res.data.chip_three),
        ]);
        setMinDeposit(Number(res.data.min_deposit));
      }
    } catch (e) {
      setConfig(null);
      setChips(null);
      setMinDeposit(null);
    }

    if (!paymentMode.length) {
      try {
        const res = await getAllData(endPoints.api.GET_PAYMENTS);
        if (res?.status === "success") setPaymentMode(res?.data || []);
      } catch (e) {
        setPaymentMode([]);
      }
    }

    if (currencies.length) return;
    try {
      const list = await fetchCurrencyList();
      setCurrencies(list || []);
    } catch (e) {
      setCurrencies([]);
    }
  };

  // Quick deposit is just a package whose price the player picked, so it hands
  // off to the same PaymentModal the package tiles use.
  const handleDeposit = ({ amount, currency }) => {
    if (!config?.custom_package_id) {
      showErrorSnackbar(
        t("paymentModal.depositUnavailable", "Deposit is not configured yet")
      );
      return;
    }
    setInitialCrypto(currency || null);
    setOpen(false);
    setSelectedPackage({
      id: config.custom_package_id,
      purchase_price: Number(amount),
      currency: IsLoginData?.currency || "USD",
      package_name: t("depositModal.customPackageName", "Custom Deposit"),
      bonus_amount: Number(amount) * Number(config.bonus_multiplier || 0),
      multplier: 1,
      freeSpin: false,
    });
    setPayModal(true);
  };

  return (
    <QuickDepositContext.Provider value={{ openQuickDeposit }}>
      {children}

      <DepositModal
        open={open}
        onClose={() => setOpen(false)}
        onBuyPackage={() => {
          setOpen(false);
          navigate("/buy-coins");
        }}
        cryptoCurrency={currencies}
        depositConfig={config}
        chips={chips}
        minDeposit={minDeposit}
        onDeposit={handleDeposit}
      />

      <PaymentModal
        open={payModal}
        onClose={() => {
          setPayModal(false);
          setInitialCrypto(null);
        }}
        selecteNewPackage={selectedPackage}
        GetUserById={refreshUser}
        paymentMode={paymentMode}
        initialCrypto={initialCrypto}
      />
    </QuickDepositContext.Provider>
  );
};

export default QuickDepositProvider;
